import { useMemo, useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { ArrowDown, ArrowUp, GripVertical, ListChecks, Lock, RotateCcw } from 'lucide-react';
import {
  FALLBACK_RECEIPT_RUBRIC_CATALOG,
  RECEIPT_PRESENTATION_MODES,
  buildDefaultReceiptRubrics,
  isRubricVisibleByDefault,
  normalizeReceiptRubrics,
} from '@/lib/payrollReceiptRubrics';

const KIND_LABELS = { quantity: 'Qtd.', money: 'Valor', text: 'Texto' };
const COLUMN_LABELS = { quantity: 'Coluna Qtd./Dias', earnings: 'Rendimentos', deductions: 'Descontos', net: 'Linha final' };

const ModeCard = ({ mode, active, onSelect }) => (
  <button
    type="button"
    onClick={() => onSelect(mode.value)}
    className={`rounded-2xl border px-3 py-2.5 text-left transition-colors ${active ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'}`}
    data-testid={`receipt-rubrics-mode-${mode.value}`}
    aria-pressed={active}
  >
    <p className="text-sm font-semibold">{mode.label}</p>
    <p className={`mt-0.5 text-[11px] leading-snug ${active ? 'text-slate-200' : 'text-slate-500'}`}>{mode.description}</p>
  </button>
);

const RubricRow = ({ rubric, meta, index, total, dragging, onDragStart, onDragEnd, onDrop, onUpdate, onMove }) => (
  <div
    className={`grid grid-cols-[36px_minmax(0,1.4fr)_minmax(0,1fr)_132px_64px_84px] items-center gap-2 border-b border-slate-200 px-3 py-2 last:border-b-0 ${dragging ? 'bg-sky-50' : 'bg-white/70'}`}
    data-testid={`receipt-rubric-row-${rubric.key}`}
    onDragOver={(event) => event.preventDefault()}
    onDrop={() => onDrop(rubric.key)}
  >
    <button
      type="button"
      draggable
      onDragStart={() => onDragStart(rubric.key)}
      onDragEnd={onDragEnd}
      className="flex h-8 w-8 items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500"
      data-testid={`receipt-rubric-drag-handle-${rubric.key}`}
      aria-label={`Arrastar ${meta.default_label}`}
    >
      <GripVertical className="h-4 w-4" />
    </button>
    <div className="min-w-0">
      <Input
        value={rubric.label || ''}
        onChange={(event) => onUpdate(rubric.key, { label: event.target.value })}
        className="h-8 rounded-lg border-slate-200 bg-white text-sm"
        data-testid={`receipt-rubric-label-input-${rubric.key}`}
      />
      <p className="mt-0.5 truncate text-[10px] text-slate-400">Original: {meta.default_label}</p>
    </div>
    <div className="flex min-w-0 flex-wrap items-center gap-1">
      <Badge variant="outline" className="rounded-full text-[10px]">{KIND_LABELS[meta.kind] || meta.kind}</Badge>
      <Badge variant="outline" className="rounded-full text-[10px]">{COLUMN_LABELS[meta.column] || meta.column}</Badge>
      {meta.conditional ? <Badge variant="outline" className="rounded-full border-amber-200 bg-amber-50 text-[10px] text-amber-700">Só com dados</Badge> : null}
    </div>
    <div>
      {meta.mandatory ? (
        <Badge className="gap-1 rounded-full bg-slate-900 text-[10px] text-white" data-testid={`receipt-rubric-mandatory-badge-${rubric.key}`}>
          <Lock className="h-3 w-3" /> Obrigatória
        </Badge>
      ) : (
        <Badge variant="outline" className="rounded-full text-[10px] text-slate-600">Opcional</Badge>
      )}
    </div>
    <div className="flex items-center justify-center">
      <Switch
        checked={meta.mandatory ? true : Boolean(rubric.visible)}
        disabled={meta.mandatory}
        onCheckedChange={(checked) => onUpdate(rubric.key, { visible: checked })}
        data-testid={`receipt-rubric-visible-toggle-${rubric.key}`}
      />
    </div>
    <div className="flex items-center justify-end gap-1">
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => onMove(rubric.key, 'up')} disabled={index === 0} data-testid={`receipt-rubric-move-up-${rubric.key}`}>
        <ArrowUp className="h-4 w-4" />
      </Button>
      <Button type="button" variant="ghost" size="icon" className="h-8 w-8 rounded-lg" onClick={() => onMove(rubric.key, 'down')} disabled={index === total - 1} data-testid={`receipt-rubric-move-down-${rubric.key}`}>
        <ArrowDown className="h-4 w-4" />
      </Button>
    </div>
  </div>
);

export const PayrollReceiptRubricsEditor = ({ currentSettings, catalog, onFieldChange }) => {
  const [dragKey, setDragKey] = useState(null);
  const effectiveCatalog = catalog?.length ? catalog : FALLBACK_RECEIPT_RUBRIC_CATALOG;
  const catalogByKey = useMemo(() => new Map(effectiveCatalog.map((item) => [item.key, item])), [effectiveCatalog]);
  const rubrics = useMemo(() => normalizeReceiptRubrics(currentSettings?.receipt_rubrics, effectiveCatalog), [currentSettings?.receipt_rubrics, effectiveCatalog]);
  const mode = currentSettings?.receipt_presentation_mode || 'complete';
  const hiddenCount = rubrics.filter((item) => !item.visible).length;
  const mandatoryCount = effectiveCatalog.filter((item) => item.mandatory).length;

  const commit = (nextRubrics, nextMode = mode) => {
    onFieldChange('receipt_rubrics', nextRubrics.map((item, index) => ({ ...item, order: index })));
    if (nextMode !== mode) onFieldChange('receipt_presentation_mode', nextMode);
  };

  const handleSelectMode = (value) => {
    if (value === 'custom') {
      onFieldChange('receipt_presentation_mode', 'custom');
      return;
    }
    commit(rubrics.map((item) => ({ ...item, visible: isRubricVisibleByDefault(catalogByKey.get(item.key)) })), value);
  };

  const handleUpdate = (key, patch) => {
    const next = rubrics.map((item) => (item.key === key ? { ...item, ...patch } : item));
    const touchedVisibility = Object.prototype.hasOwnProperty.call(patch, 'visible');
    commit(next, touchedVisibility && mode !== 'custom' ? 'custom' : mode);
  };

  const handleMove = (key, direction) => {
    const index = rubrics.findIndex((item) => item.key === key);
    const target = direction === 'up' ? index - 1 : index + 1;
    if (index === -1 || target < 0 || target >= rubrics.length) return;
    const next = [...rubrics];
    [next[index], next[target]] = [next[target], next[index]];
    commit(next);
  };

  const handleDrop = (targetKey) => {
    if (!dragKey || dragKey === targetKey) return;
    const next = [...rubrics];
    const from = next.findIndex((item) => item.key === dragKey);
    const to = next.findIndex((item) => item.key === targetKey);
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    commit(next);
    setDragKey(null);
  };

  const handleReset = () => commit(buildDefaultReceiptRubrics(effectiveCatalog), 'complete');

  return (
    <div className="rounded-3xl border border-slate-200 bg-white p-4" data-testid="payroll-receipt-rubrics-editor">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <ListChecks className="h-4 w-4 text-slate-700" />
            <h3 className="text-sm font-semibold text-slate-900">Rubricas do recibo</h3>
          </div>
          <p className="mt-1 max-w-2xl text-xs text-slate-600">
            Controla apenas a <span className="font-semibold text-slate-800">apresentação</span> do recibo: visibilidade, ordem e designação de cada rubrica.
            O cálculo da folha, INSS, IRPS e líquido nunca é alterado por este editor.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Badge variant="outline" className="rounded-full" data-testid="receipt-rubrics-summary-badge">{rubrics.length} rubricas · {mandatoryCount} obrigatórias · {hiddenCount} ocultas</Badge>
          <Button type="button" variant="ghost" className="h-8 rounded-xl px-2 text-xs" onClick={handleReset} data-testid="receipt-rubrics-reset-button">
            <RotateCcw className="mr-1 h-3.5 w-3.5" /> Repor predefinição
          </Button>
        </div>
      </div>

      <div className="mt-3 space-y-1.5">
        <Label className="text-xs font-medium text-slate-600">Modelo de apresentação</Label>
        <div className="grid gap-2 md:grid-cols-3" data-testid="receipt-rubrics-mode-selector">
          {RECEIPT_PRESENTATION_MODES.map((item) => (
            <ModeCard key={item.value} mode={item} active={mode === item.value} onSelect={handleSelectMode} />
          ))}
        </div>
      </div>

      <div className="mt-3 overflow-x-auto rounded-2xl border border-slate-200 bg-slate-50/70">
        <div className="min-w-[760px]">
          <div className="grid grid-cols-[36px_minmax(0,1.4fr)_minmax(0,1fr)_132px_64px_84px] gap-2 border-b border-slate-200 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
            <span></span>
            <span>Designação no recibo</span>
            <span>Tipo / coluna</span>
            <span>Regra</span>
            <span className="text-center">Visível</span>
            <span className="text-right">Ordem</span>
          </div>
          {rubrics.map((rubric, index) => (
            <RubricRow
              key={rubric.key}
              rubric={rubric}
              meta={catalogByKey.get(rubric.key)}
              index={index}
              total={rubrics.length}
              dragging={dragKey === rubric.key}
              onDragStart={setDragKey}
              onDragEnd={() => setDragKey(null)}
              onDrop={handleDrop}
              onUpdate={handleUpdate}
              onMove={handleMove}
            />
          ))}
        </div>
      </div>
    </div>
  );
};
